import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { IntakeMode, QueueRow, TargetPms } from "@shared/types";
import { TARGET_PMS_LABEL, allowedPmsForStore } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";
const STORAGE_MODE_KEY  = "smartrx-entry.intakeMode";
const STORAGE_PMS_KEY   = "smartrx-entry.targetPms";

// Community Pharmacy store codes — only these run vaccine intake (PioneerRx sites).
const VACCINE_STORE_CODES = new Set(["COR", "DEN", "FM"]);

function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

function getInitialMode(storeCode: string): IntakeMode {
  const saved = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_MODE_KEY);
  if (saved === "vaccine" && VACCINE_STORE_CODES.has(storeCode)) return "vaccine";
  return "rx";
}

function getInitialPms(storeCode: string): TargetPms {
  const allowed = allowedPmsForStore(storeCode);
  const saved = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_PMS_KEY) as TargetPms | null : null;
  return saved && allowed.includes(saved) ? saved : allowed[0];
}

export function Home() {
  const navigate = useNavigate();
  const storeCode = getCurrentStoreCode();
  const vaccineAllowed = VACCINE_STORE_CODES.has(storeCode);
  const allowedPms = allowedPmsForStore(storeCode);
  const [mode, setMode] = useState<IntakeMode>(() => getInitialMode(storeCode));
  const [targetPms, setTargetPms] = useState<TargetPms>(() => getInitialPms(storeCode));

  function selectPms(next: TargetPms) {
    setTargetPms(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_PMS_KEY, next);
  }
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function selectMode(next: IntakeMode) {
    setMode(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_MODE_KEY, next);
  }

  function loadQueue() {
    const ctrl = new AbortController();
    apiGet<QueueRow[]>(`/queue?store=${storeCode}&mode=${mode}`, ctrl.signal)
      .then((data) => { setRows(data); setQueueError(null); })
      .catch((e) => { setRows([]); setQueueError(String(e)); });
    return () => ctrl.abort();
  }

  useEffect(() => loadQueue(), [mode, storeCode]);

  async function handleFile(file: File) {
    setUploadError(null);
    if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) {
      setUploadError("Only PDF, PNG, or JPG are supported.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setUploadError("File too large (max 20 MB).");
      return;
    }
    setUploading(true);
    try {
      // Demo mode: save the uploaded file as a blob URL so the Review page
      // shows it in the document panel.
      try {
        const blobUrl = URL.createObjectURL(file);
        window.sessionStorage.setItem("demo.lastUpload.url", blobUrl);
        window.sessionStorage.setItem("demo.lastUpload.name", file.name);
        window.sessionStorage.setItem("demo.lastUpload.mime", file.type);
      } catch { /* ignore */ }

      const base64 = await fileToBase64(file);
      const { rxIds } = await apiPost<{ rxIds: number[] }>("/intakes/upload", {
        storeCode,
        mode,
        targetPms,
        filename: file.name,
        contentType: file.type,
        base64,
      });
      if (rxIds.length > 0) navigate(`/review/${rxIds[0]}`);
      else loadQueue();
    } catch (e) {
      setUploadError(String(e));
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleFile(file);
  }

  const noun         = mode === "vaccine" ? "vaccine claim" : "prescription";
  const dropTitle    = mode === "vaccine" ? "Drop a vaccine consent form" : "Drop a faxed or written prescription";
  const dropSubtitle = mode === "vaccine"
    ? "PDF, PNG, or JPG (max 20 MB). The AI extracts patient, vaccine, and Medicare fields — tech verifies anything under the auto-approve threshold."
    : "PDF, PNG, or JPG (max 20 MB). The AI extracts fields and routes the Rx to your review queue.";
  const emptyCopy    = mode === "vaccine"
    ? "Nothing in the review queue yet. Upload a consent form above to see it appear here after the AI extracts."
    : "Nothing in the review queue yet. Upload a prescription above to see it appear here after the AI extracts.";

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-1">Good afternoon, Ashley</h2>
      <p className="text-ink-soft mb-4">
        {queueError
          ? <span className="text-bad">Queue unavailable: {queueError}</span>
          : `${rows.length} ${noun}${rows.length === 1 ? "" : "s"} awaiting tech review`}
      </p>

      <div className="flex gap-3 items-center mb-4">
        {vaccineAllowed && (
          <div className="inline-flex rounded border border-border overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => selectMode("rx")}
              className={`px-4 py-1.5 ${mode === "rx" ? "bg-accent text-white" : "bg-surface text-ink-soft hover:bg-pill"}`}
            >
              New Rx
            </button>
            <button
              type="button"
              onClick={() => selectMode("vaccine")}
              className={`px-4 py-1.5 border-l border-border ${mode === "vaccine" ? "bg-accent text-white" : "bg-surface text-ink-soft hover:bg-pill"}`}
            >
              New Vaccine
            </button>
          </div>
        )}

        <div className="inline-flex items-center gap-2 text-sm">
          <span className="text-ink-soft">Processing software:</span>
          <select value={targetPms} onChange={(e) => selectPms(e.target.value as TargetPms)}
            className="border border-border rounded px-2 py-1 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-accent">
            {allowedPms.map((p) => <option key={p} value={p}>{TARGET_PMS_LABEL[p]}</option>)}
          </select>
        </div>
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="bg-accent-soft border border-dashed border-accent rounded-lg p-6 text-center mb-6"
      >
        <h3 className="text-accent font-semibold mb-1">{dropTitle}</h3>
        <p className="text-ink-soft text-sm mb-3">{dropSubtitle}</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf,image/png,image/jpeg"
          hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ""; }}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="px-4 py-2 bg-white border border-accent text-accent rounded text-sm font-medium hover:bg-accent-soft disabled:opacity-40"
        >
          {uploading ? "Uploading + extracting…" : "Choose file…"}
        </button>
        {uploadError && <p className="text-bad text-sm mt-3">{uploadError}</p>}
      </div>

      <div className="bg-surface border border-border rounded-lg p-4">
        <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-3">Tech review queue — top of stack</h3>
        {rows.length === 0 && !queueError && (
          <p className="text-ink-soft text-sm py-6 text-center">{emptyCopy}</p>
        )}
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.rxId} className="grid grid-cols-[80px_1fr_90px_auto_80px] gap-3 py-2.5 items-center text-sm">
              <span className="text-ink-soft">#{r.rxId}</span>
              <span><strong>{r.patientName}</strong> · {r.drug}</span>
              <span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${confClass(r.rxConfidence)}`}>
                  {r.rxConfidence?.toFixed(2) ?? "—"}
                </span>
              </span>
              <span className="flex gap-1">
                {r.isControlled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-warn-soft text-warn">controlled</span>}
                {r.isCompound && <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-soft text-accent">compound</span>}
                {r.species !== "human" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ecf6e7] text-[#4d7626]">vet</span>}
              </span>
              <Link
                to={`/review/${r.rxId}`}
                className="text-center px-3 py-1 rounded bg-accent text-white text-xs font-medium hover:brightness-95"
              >
                Review
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") return reject(new Error("FileReader returned non-string"));
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function confClass(c: number | null): string {
  if (c === null) return "bg-pill text-ink-soft";
  if (c >= 0.85) return "bg-good-soft text-good";
  if (c >= 0.6)  return "bg-warn-soft text-warn";
  return "bg-bad-soft text-bad";
}
